import { Alert, Box, Typography } from '@mui/material';
import Breadcrumbs from 'components/breadcrumbs';
import FilterSidebar from 'components/filter-sidebar';
import FlexBox from 'components/flex-box';
import Paper from 'components/paper';

import ASCSmallCheck from 'components/asc/asc-small-check';
import { useAccountState } from 'contexts/account/account-context';
import { AddressAccess } from 'contexts/account/types/account-company.type';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useParams } from 'react-router';
import OrderFeedTabsHeader from 'views/order-feed/components/order-feed-tabs-header';
import { OrderFeedProvider } from 'views/order-feed/context/order-feed-context';
import { OrderFeedSettingsProvider, useOrderFeedSettingsState } from 'views/order-feed/context/order-feed-settings-context';
import { MySupplyPlanProvider } from 'views/order-feed/features/my-supply-plan/context/my-supply-plan-context';
import OrderList from 'views/order-feed/features/my-supply-plan/features/ofd-1.0/components/order-list/order-list';
import { MySupplyPlanParams } from 'views/order-feed/features/my-supply-plan/types/my-supply-plan-params.type';
import SelectSite from 'views/playground/features/my-supply-plan/components/select-site';
import { AscCertificationStatus } from 'views/site-settings/types/asc-certification-status.type';
import PlaygroundTabsHeader from 'views/playground/components/playground-tabs-header';

type Props = {};

const ListOrderSupplyPlanView: React.FC<Props> = () => {
  const params = useParams<MySupplyPlanParams>();
  const history = useHistory();
  const intl = useIntl();
  const { account } = useAccountState();
  const {
    orderSettingsQuery: { data: orderSettings },
    siteSettingsQuery: { data: siteSettings },
  } = useOrderFeedSettingsState();
  const isAscCompliant = siteSettings?.ascStatus === AscCertificationStatus.AscCertified;

  // #region STATE

  const [selectedSiteId, setSelectedSiteId] = useState<string>('');

  // #endregion STATE

  // #region HELPERS

  const isSiteActiveInErp = (site: AddressAccess) => !!site.isActive && !!site.siteErpId && !!site.address_1;

  // #endregion HELPERS

  // #region HOOKS

  useEffect(() => {
    setSelectedSiteId(
      params.siteErpId && params.globalSiteId ? `${String(params.siteErpId)}/${String(params.globalSiteId)}` : ''
    );
  }, [params.siteErpId, params.globalSiteId]);

  useEffect(() => {
    if (account.company?.addressAccess.length === 1 && isSiteActiveInErp(account.company?.addressAccess[0])) {
      history.push(
    `/playground/supply-plan/${String(account.company?.addressAccess[0].siteErpId)}/${String(
          account.company?.addressAccess[0].id
        )}`
      );
    }
  }, [account.company?.addressAccess, history]);

  // #endregion HOOKS

  return (
    <>
      <PlaygroundTabsHeader
        utils={
          <Breadcrumbs
            crumbs={
              selectedSiteId
                ? [
                    {
                      text:
                        account.company?.addressAccess.find((s) => s.siteErpId === params.siteErpId)?.name || selectedSiteId,
                      url: `${params.siteErpId}/${params.globalSiteId}`,
                    },
                  ]
                : undefined
            }
          />
        }
      />
      {!!orderSettings?.isUnderMaintenance && (
        <Alert severity="warning" sx={{ mb: 4 }}>
          <FormattedMessage id={`orderFeed.alert.maintenance`} />
          {orderSettings?.endsAt && (
            <Typography sx={{ display: 'flex', alignItems: 'center' }}>
              <FormattedMessage id={`orderFeed.alert.endsAt`} />
              <FormattedDate
                value={dayjs(orderSettings.endsAt).toDate()}
                year="numeric"
                month="long"
                day="2-digit"
                hour="2-digit"
                minute="2-digit"
              />
            </Typography>
          )}
        </Alert>
      )}
      <Box>
        <FilterSidebar>
          <SelectSite />
        </FilterSidebar>
        <Paper transparent>
          <Box sx={{ minWidth: 150 }}>{selectedSiteId && isAscCompliant && <ASCSmallCheck />}</Box>
          <FlexBox>
            <OrderList />
          </FlexBox>
        </Paper>
      </Box>
    </>
  );
};

const ListOrderSupplyPlan: React.FC<Props> = () => (
  <OrderFeedProvider>
    <OrderFeedSettingsProvider>
      <MySupplyPlanProvider>
        <ListOrderSupplyPlanView />
      </MySupplyPlanProvider>
    </OrderFeedSettingsProvider>
  </OrderFeedProvider>
);

export default ListOrderSupplyPlan;
